/**
 * Общий кадр позы headless-телефона (GEN-18912): `{type: '…BodyPoseData', pose: {nose, leftShoulder, …}}`
 * ~30 раз/с по DataChannel, без привязки к игре. Точки 0…1 в зеркальном кадре камеры, y вниз; значения за краем
 * кадра не обрезаются (рука за рамкой — тоже позиция), отбрасываются только абсурдные (|v| > 10). Сустав, которого
 * телефон не видит, в кадре отсутствует; пустой `pose` — человека в кадре нет. Кадр без объекта `pose` — битый и
 * отбрасывается.
 */
export const BODY_POSE_JOINTS = [
  'nose',
  'leftShoulder', 'rightShoulder',
  'leftElbow', 'rightElbow',
  'leftWrist', 'rightWrist',
  'leftIndex', 'rightIndex',
  'leftHip', 'rightHip',
] as const;

export type BodyJoint = (typeof BODY_POSE_JOINTS)[number];

export interface BodyPoint {
  x: number;
  y: number;
}

/** Только видимые суставы. */
export type BodyPose = Partial<Record<BodyJoint, BodyPoint>>;

/** Ширина плеч (доля кадра), меньше которой «плечи» — шум детектора, а не человек. */
export const MIN_SHOULDER_WIDTH = 0.02;

/**
 * Человек в кадре: видны оба плеча. Когда камера пуста, детектор иногда отдаёт одиночный сустав (запястье, нос) —
 * он скачет по кадру, и принимать его за игрока нельзя: ни курсором, ни скелетом.
 */
export function bodyPresent(pose: BodyPose | null): boolean {
  if (!pose) return false;
  const { leftShoulder: ls, rightShoulder: rs } = pose;
  return ls !== undefined && rs !== undefined && Math.abs(rs.x - ls.x) >= MIN_SHOULDER_WIDTH;
}

/** Тип кадра — имя класса у Kotlin (`io.oitech…GameMessage.BodyPoseData`), поэтому по подстроке. */
export function isBodyPoseFrame(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const { type } = raw as Record<string, unknown>;
  return typeof type === 'string' && type.includes('BodyPoseData');
}

/** Норма 0…1, с запасом за край кадра. Телефону не доверяем: `1e308` дал бы `Infinity`/`NaN` в фильтре сглаживания. */
const COORD_LIMIT = 10;

function parsePoint(value: unknown): BodyPoint | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number'
    && Number.isFinite(x) && Number.isFinite(y)
    && Math.abs(x) <= COORD_LIMIT && Math.abs(y) <= COORD_LIMIT ? { x, y } : null;
}

export function parseBodyPoseFrame(raw: unknown): BodyPose | null {
  if (!isBodyPoseFrame(raw)) return null;
  const { pose } = raw as Record<string, unknown>;
  if (!pose || typeof pose !== 'object' || Array.isArray(pose)) return null;
  const result: BodyPose = {};
  BODY_POSE_JOINTS.forEach((joint) => {
    const point = parsePoint((pose as Record<string, unknown>)[joint]);
    if (point) result[joint] = point;
  });
  return result;
}
